'use client'

import classNames from 'classnames'
import React, {
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ButtonContext } from '@/app/_providers/ButtonProvider/ButtonProvider'
import StepContext from '@/app/_providers/StepProvider/StepContext'
import { AnchorButton } from '@/app/_components/AnchorButton'
import { Button } from '@/app/_components/Button'
import { ButtonProps, ButtonSize, ButtonType, ButtonVariant } from '@/types/Button.types'
import styles from './ActionButton.module.css'
import TXT from '@/messages'

type Props = {
  as?: ButtonType
  testId?: string
  note?: string
  action?: string
  buttonText?: string | ReactNode
  simpleButton?: boolean
  icon?: ReactNode
  className?: string
  href?: string
  onClick?: () => void
  disabled?: boolean
  portalSelector?: string
  size?: ButtonSize
  variant?: ButtonVariant
  defaultButtonHidden?: boolean
}

const ButtonWrapper: FunctionComponent<PropsWithChildren<ButtonProps & { asAnchor: boolean }>> = ({
  children,
  asAnchor,
  ...props
}) => {
  if (asAnchor) {
    return <AnchorButton {...props}>{children}</AnchorButton>
  }

  return <Button {...props}>{children}</Button>
}

const ActionButton: FunctionComponent<Props> = ({
  testId,
  action,
  note,
  buttonText,
  simpleButton,
  portalSelector,
  icon,
  className,
  disabled,
  onClick,
  defaultButtonHidden,
  as: tagAs,
  size,
  variant,
  href,
}): ReactNode => {
  const { buttonState, loading } = useContext(ButtonContext)
  const { isStepSynced } = useContext(StepContext)
  const [isTemporarilyDisabled, setIsTemporarilyDisabled] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Until the step catches up with a route change, the shared state still holds the previous step's action
  const isStaleSharedAction = !onClick && !isStepSynced

  const resolvedButtonText = buttonText ?? buttonState.text
  const resolvedDisabled = Boolean(disabled ?? buttonState.disabled)
    || loading || isTemporarilyDisabled || isStaleSharedAction

  const handleOnClick = useCallback((): void => {
    if (resolvedDisabled) {
      return
    }

    setIsTemporarilyDisabled(true);
    (onClick ?? buttonState.action)()
    timeoutRef.current = setTimeout(() => {
      setIsTemporarilyDisabled(false)
    }, 1000)
  }, [buttonState.action, onClick, resolvedDisabled])

  useEffect(() => (
    () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  ), [])

  return (
    <div
      className={classNames(
        { [`${className}-container`]: className },
        styles.ActionButtonWrapper,
      )}
    >
      {note && <div className={styles.Note}>{note}</div>}
      {action && <div className={styles.Action}>{action}</div>}
      {!defaultButtonHidden && resolvedButtonText && !simpleButton && (
        <ButtonWrapper
          asAnchor={tagAs === 'a'}
          testId={testId || 'action-button'}
          className={classNames(className, { [styles.Disabled]: resolvedDisabled })}
          disabled={resolvedDisabled}
          href={href}
          onClick={handleOnClick}
          icon={icon}
          variant={variant}
          size={size || 'xl'}
          weight="bold"
        >
          {loading ? TXT.buttons.loading : resolvedButtonText}
        </ButtonWrapper>
      )}
      {!defaultButtonHidden && resolvedButtonText && simpleButton && (
        <button
          data-testid={testId || 'action-button'}
          className={classNames(styles.SimpleButton, className, { [styles.Disabled]: resolvedDisabled })}
          disabled={resolvedDisabled}
          onClick={handleOnClick}
          type="button"
        >
          {loading ? TXT.buttons.loading : resolvedButtonText}
        </button>
      )}
      {portalSelector && <div id={portalSelector} />}
    </div>
  )
}

export default ActionButton
